import { useMemo } from 'react';
import { GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Top149SchoolYearStatsSchema } from '../../../services';
import { z } from 'zod';
import Box from '@mui/material/Box/Box';
import Typography from '@mui/material/Typography';
import { useAppSelector } from '../../../store/hooks';
import { MOBILE_CELL_STYLES, MOBILE_GUIDE } from '../../../utils/responsive';

type Top149SchoolStats = z.infer<typeof Top149SchoolYearStatsSchema>;

interface UseTop149ColumnsProps {
	admissionStats?: { stats: z.infer<typeof Top149SchoolYearStatsSchema>[] };
	isAdmin: boolean;
	isMobile?: boolean;
	onEdit: (school: Top149SchoolStats) => () => void;
	onDelete: () => () => void;
}

export function useTop149Columns({ isAdmin, admissionStats, isMobile = false, onEdit: _onEdit, onDelete }: UseTop149ColumnsProps) {
	const years = useAppSelector((state) => state.filters.years);

	return useMemo<GridColDef[]>(
		() => {
			if (isMobile) {
				const sortedYears = [...years].sort((a: number, b: number) => a - b);
				const latestYear = sortedYears[sortedYears.length - 1];
				return [
					{
						field: 'tenTruong',
						headerName: 'Tên Trường',
						minWidth: MOBILE_CELL_STYLES.MIN_WIDTH,
						flex: 1,
						sortable: true,
						filterable: false,
						renderCell: ({ row }) => {
							const schoolData = admissionStats?.stats?.find(
									(stat) => stat.maTruong === row.maTruong
							);
							const latestCount = latestYear && schoolData?.yearData ? schoolData.yearData[latestYear] || 0 : 0;
							return (
								<Box
									sx={{
										width: '100%',
										height: '100%',
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'flex-start',
										justifyContent: 'center',
										padding: MOBILE_CELL_STYLES.PADDING,
										whiteSpace: 'normal',
										wordWrap: 'break-word',
										lineHeight: MOBILE_CELL_STYLES.LINE_HEIGHT,
										gap: MOBILE_CELL_STYLES.GAP,
									}}
								>
									<Typography variant="caption" color="text.secondary">
										{row.maTruong || 'N/A'}
									</Typography>
									<Typography variant="body2">
										{row.tenTinh || 'N/A'}
									</Typography>
									<Typography variant="body2" fontWeight="bold">
										{row.tenTruong || 'N/A'}
									</Typography>
									{latestYear && (
										<Typography variant="caption" color="text.secondary">
											{latestYear}: {latestCount} sinh viên
										</Typography>
									)}
									<Typography variant="caption" color={MOBILE_GUIDE.COLOR} fontWeight={MOBILE_GUIDE.FONT_WEIGHT}>
										{MOBILE_GUIDE.TEXT}
									</Typography>
								</Box>
							);
						},
					},
				];
			}

			const baseColumns: GridColDef[] = [
				{
					field: 'stt',
					headerName: 'STT',
					width: 60,
					headerAlign: 'center',
					align: 'center',
					sortable: false,
					filterable: false,
					renderCell: ({ api, id }) => {
						const currentPage = api.state.pagination.paginationModel.page;
						const pageSize = api.state.pagination.paginationModel.pageSize;
						const allRows = api.getRowModels();
						const rowIndex = Array.from(allRows.keys()).indexOf(id as string);
						const stt = currentPage * pageSize + rowIndex + 1;
						return stt;
					},
				},
				{
					field: 'maTruong',
					headerName: 'Mã Trường',
					width: 150,
					headerAlign: 'center',
					align: 'center',
					renderCell: ({ value }) => value || 'N/A',
					sortable: true,
					filterable: false,
				},
				{
					field: 'tenTruong',
					headerName: 'Tên Trường',
					width: 300,
					headerAlign: 'center',
					sortable: true,
					filterable: false,
					renderCell: ({ value }) => (
						<Box
							sx={{
								width: '100%',
								height: '100%',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'flex-start',
								padding: '8px',
								whiteSpace: 'normal',
								wordWrap: 'break-word',
								lineHeight: 1.2,
							}}
						>
							{value || 'N/A'}
						</Box>
					),
				},
				{
					field: 'tenTinh',
					headerName: 'Tỉnh/Thành phố',
					width: 150,
					headerAlign: 'center',
					sortable: true,
					filterable: true,
				},
				{
					field: 'tenTinhSauSapNhap',
					headerName: 'Sau sáp nhập',
					width: 150,
					headerAlign: 'center',
					sortable: true,
					filterable: true,
					renderCell: ({ value }) => value || 'N/A',
				},
				{
					field: 'maTruongSauSapNhap',
					headerName: 'Mã trường sau sáp nhập',
					width: 170,
					headerAlign: 'center',
					align: 'center',
					sortable: false,
					filterable: false,
					renderCell: ({ value }) => value || 'N/A',
				},
				{
					field: 'hasCodeNameChange',
					headerName: 'Có thay đổi mã/tên',
					width: 120,
					type: 'singleSelect',
					valueOptions: [
						{ value: true, label: 'Có' },
						{ value: false, label: 'Không' }
					],
					headerAlign: 'center',
					align: 'center',
					sortable: true,
					filterable: true,
					renderCell: ({ value }) => (
						<Box
							sx={{
								width: '100%',
								height: '100%',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								color: value ? 'warning.main' : 'text.secondary',
								fontWeight: value ? 'bold' : 'normal'
							}}
						>
							{value ? 'Có' : 'Không'}
						</Box>
					),
				},
			];

			// Create year columns dynamically following faculty pattern
			const yearColumns: GridColDef[] = [];
			if (years.length > 0 && admissionStats?.stats) {
				const sortedYears = [...years].sort((a: number, b: number) => a - b);

				sortedYears.slice(-5).forEach((year: number) => {
					yearColumns.push({
						field: year.toString(),
						headerName: year.toString(),
						width: 80,
						type: 'number',
						headerAlign: 'center',
						align: 'center',
						sortable: true,
						filterable: false,
						renderCell: ({ row }: { row: Top149SchoolStats }) => {
							const schoolData = admissionStats.stats.find(
								(stat) => stat.maTruong === row.maTruong
							);
							const count = schoolData?.yearData?.[year] || 0;
							return count;
						},
					});
				});
			}

			// Actions column
			const actionColumn = isAdmin ? [{
				field: 'actions' as const,
				type: 'actions' as const,
				headerName: 'Thao tác',
				width: 120,
				headerAlign: 'center' as const,
				getActions: () => [
					<GridActionsCellItem
						key="delete-item"
						icon={<DeleteIcon />}
						label="Xóa"
						onClick={onDelete()}
					/>,
				],
			}] : [];

			return [...baseColumns, ...yearColumns, ...actionColumn];
		},
		[isAdmin, years, admissionStats, isMobile, onDelete],
	);
}